import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { AuthStore } from '@store/auth.store';

type AuthStoreInstance = InstanceType<typeof AuthStore>;

const AUTH_PATH = '/auth';
const GEO_API_URL = environment.geoApiUrl;
const UNAUTHORIZED = 401;
const MAX_REFRESH_RETRIES = 1;

let refreshRequest$: Observable<boolean> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  if (req.url.includes(AUTH_PATH) || (!!GEO_API_URL && req.url.startsWith(GEO_API_URL))) {
    return next(req);
  }

  const token = authStore.accessToken();
  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== UNAUTHORIZED) {
        return throwError(() => error);
      }

      if (refreshRequest$) {
        return refreshRequest$.pipe(
          switchMap(success => retryWithFreshToken(success, authStore, req, next, error)),
          catchError(() => throwError(() => error))
        );
      }

      refreshRequest$ = refreshWithRetry(authStore, MAX_REFRESH_RETRIES).pipe(
        tap(success => {
          if (!success) {
            authStore.logout(router.url);
          }
        }),
        catchError(refreshError => {
          authStore.logout(router.url);
          return throwError(() => refreshError as Error);
        }),
        finalize(() => {
          refreshRequest$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

      return refreshRequest$.pipe(
        switchMap(success => retryWithFreshToken(success, authStore, req, next, error)),
        catchError(refreshError => throwError(() => refreshError as Error))
      );
    })
  );
};

function retryWithFreshToken(
  success: boolean,
  authStore: AuthStoreInstance,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse
): Observable<HttpEvent<unknown>> {
  const newToken = authStore.accessToken();
  if (!success || !newToken) {
    return throwError(() => error);
  }
  return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
}

/**
 * Retry refresh with exponential backoff: immediate → 1s → 2s
 */
function refreshWithRetry(
  authStore: AuthStoreInstance,
  remainingRetries: number
): Observable<boolean> {
  return authStore.refresh().pipe(
    switchMap(success => {
      if (success) {
        return of(true);
      }
      if (remainingRetries <= 1) {
        return of(false);
      }
      const backoffMs = Math.pow(2, MAX_REFRESH_RETRIES - remainingRetries) * 1000;
      return timer(backoffMs).pipe(
        switchMap(() => refreshWithRetry(authStore, remainingRetries - 1))
      );
    }),
    catchError(() => {
      if (remainingRetries <= 1) {
        return of(false);
      }
      const backoffMs = Math.pow(2, MAX_REFRESH_RETRIES - remainingRetries) * 1000;
      return timer(backoffMs).pipe(
        switchMap(() => refreshWithRetry(authStore, remainingRetries - 1))
      );
    })
  );
}
